import { Flex, Text, Heading } from "@wayflyer/flyui";
import { IconDropDollar24Line, IconDocumentArrowUp24Line } from "@wayflyer/flyui-icons/24/line";
import { IconCreditCard24Line, IconBank24Line } from "@wayflyer/flyui-icons-next/24/line";
import type { FundingApplicationSchemas } from "api-types";
import type { ReactNode } from "react";
import { useIntl, type MessageDescriptor } from "react-intl";
import { styled } from "styled-components";

import { MESSAGES } from "../message-definitions";

const ReqiredDataContainer = styled(Flex)`
  background: var(--palette-bg-accent-subtle);
  border-radius: var(--sizes-radius-sm);
`;

interface RequiredDataSourcesProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}

interface RequiredDataSource {
  icon: ReactNode;
  titleMessage: MessageDescriptor;
  descriptionMessage: MessageDescriptor;
}

type RequiredDataSourceType = "banking" | "credit-check" | "cash-flow-forecast" | "susa" | "pool-bancario";

const REQUIRED_DATA_SOURCES: Record<RequiredDataSourceType, RequiredDataSource> = {
  banking: {
    icon: <IconBank24Line />,
    titleMessage: MESSAGES.requiredDataSourcesBankAccountsTitle,
    descriptionMessage: MESSAGES.requiredDataSourcesBankAccountsDescription,
  },
  "credit-check": {
    icon: <IconCreditCard24Line />,
    titleMessage: MESSAGES.requiredDataSourcesCreditCheckTitle,
    descriptionMessage: MESSAGES.requiredDataSourcesCreditCheckDescription,
  },
  "cash-flow-forecast": {
    icon: <IconDropDollar24Line />,
    titleMessage: MESSAGES.requiredDataSourcesRevenueProjectionsTitle,
    descriptionMessage: MESSAGES.requiredDataSourcesRevenueProjectionsDescription,
  },
  susa: {
    icon: <IconDocumentArrowUp24Line />,
    titleMessage: MESSAGES.requiredDataSourcesSusaTitle,
    descriptionMessage: MESSAGES.requiredDataSourcesSusaDescription,
  },
  "pool-bancario": {
    icon: <IconDocumentArrowUp24Line />,
    titleMessage: MESSAGES.requiredDataSourcesPoolBancarioTitle,
    descriptionMessage: MESSAGES.requiredDataSourcesPoolBancarioDescription,
  },
};

export const RequiredDataSources = ({ fundingApplicationStages }: RequiredDataSourcesProps) => {
  const intl = useIntl();

  const requiredItems: RequiredDataSource[] = [];

  if (fundingApplicationStages.banking) {
    requiredItems.push(REQUIRED_DATA_SOURCES.banking);
  }

  if (fundingApplicationStages.documents) {
    fundingApplicationStages.documents.forEach((doc) => {
      if (doc.documentType in REQUIRED_DATA_SOURCES) {
        requiredItems.push(REQUIRED_DATA_SOURCES[doc.documentType as RequiredDataSourceType]);
      }
    });
  }

  if (fundingApplicationStages.creditConsent) {
    requiredItems.push(REQUIRED_DATA_SOURCES["credit-check"]);
  }

  if (requiredItems.length === 0) {
    return null;
  }

  return (
    <ReqiredDataContainer direction="column" gap="3" padding="6">
      <Heading size="base">{intl.formatMessage(MESSAGES.requiredDataSourcesTitle)}</Heading>
      {requiredItems.map((item, index) => (
        <Flex key={index} gap="3" align="center">
          {item.icon}
          <Flex direction="column">
            <Text size="sm" fontWeight="semiBold">
              {intl.formatMessage(item.titleMessage)}
            </Text>
            <Text size="xs">{intl.formatMessage(item.descriptionMessage)}</Text>
          </Flex>
        </Flex>
      ))}
    </ReqiredDataContainer>
  );
};
